import { createFileRoute, redirect } from "@tanstack/react-router";
import { GoogleAuthProvider } from "firebase/auth";
import { useAuth } from "@/hooks/useAuth";
import { z } from "zod";

const fallback = "/dashboard" as const;

export const Route = createFileRoute("/login")({
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
    // Set when AuthProvider signs a session out for exceeding its lifetime
    // (DRA-357), so the user is told why they are back here.
    expired: z.string().optional().catch(""),
  }),
  beforeLoad: ({ context, search }) => {
    // Only treat actual dev builds as development.
    const isDevelopment = import.meta.env.DEV;

    // Wait for auth state to be fully loaded before making redirect decisions
    if (context.auth?.isInitialLoading) {
      // Don't redirect while still loading - let the component handle loading state
      return;
    }

    if (context.auth?.isAuthenticated) {
      // Check authorization: require super-admin or support claim (or dev mode) -
      // matches the backend's verifyAuthToken, which authorizes both roles.
      const isAuthorized = isDevelopment || context.auth?.isSuperAdmin || context.auth?.isSupport;

      if (isAuthorized) {
        throw redirect({ to: search.redirect || fallback });
      } else {
        throw redirect({ to: "/unauthorized" });
      }
    }
  },
  component: Login,
});

function Login() {
  const { login, isInitialLoading } = useAuth();
  const { expired } = Route.useSearch();

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();

    try {
      await login(provider);
    } catch (error) {
      console.error("[Login Component] ❌ Error during sign-in:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      // Show user-friendly error message
      alert(`Sign-in failed: ${errorMessage}`);
    }
  };

  // Show loading state while auth is being checked
  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="p-8 text-center bg-card rounded-lg shadow-md max-w-md w-full">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="p-8 text-center bg-card rounded-lg shadow-md max-w-md w-full">
        <h1 className="text-3xl font-bold mb-4">Admin Login</h1>
        {expired ? (
          <p className="mb-6 text-sm text-amber-600 dark:text-amber-500" role="status">
            Your session reached its 4 hour limit and was signed out. Sign in
            again to continue.
          </p>
        ) : (
          <p className="text-muted-foreground mb-6">
            Sign in with your Google account to access the admin panel.
          </p>
        )}
        <button
          onClick={handleGoogleSignIn}
          className="px-6 py-3 font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Sign in with Google
        </button>
      </div>
    </div>
  );
}
